import {
  ICheckbox,
  IField,
  IFormDocument,
} from 'app/services/api5-service/api.interface';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { ISPFormState } from 'common/dynamic-form/model';
import { isCheckboxControl } from 'common/dynamic-form/utils/form-preparing';
import { DocHelper } from 'utils/dochelper';

import { FieldPlainHint } from './model';

/**
 * Get checkbox control and plain hints for its options
 *
 * @param field
 * @param doc
 */
function getCheckboxWithPlainHints(
  field: IField,
  doc: IFormDocument,
): FieldPlainHint | undefined {
  const controlCheckboxWithPlainHints = field.input?.find<ICheckbox>(
    (control): control is ICheckbox =>
      isCheckboxControl(control) && control.$plainhint === 'yes',
  );
  if (!controlCheckboxWithPlainHints) {
    return;
  }

  const plainHints = DocHelper.getPlainHintsForCheckbox(
    doc,
    controlCheckboxWithPlainHints,
  );
  return { plainHints, control: controlCheckboxWithPlainHints };
}

/**
 * Get select control and plain hints for its options
 *
 * @param field
 * @param doc
 */
function getSelectWithPlainHints(
  field: IField,
  doc: IFormDocument,
): FieldPlainHint | undefined {
  const controlSelectWithPlainHints = field.select?.find(
    select => select.$plainhint === 'yes',
  );
  if (!controlSelectWithPlainHints) {
    return;
  }

  const plainHints = DocHelper.getPlainHintsForSelect(
    doc,
    controlSelectWithPlainHints,
  );

  return { plainHints, control: controlSelectWithPlainHints };
}

/**
 * Get control and plain hints for it
 *
 * @param field
 * @param doc
 */
function getFieldPlainHints(
  field: IField,
  doc: IFormDocument,
): FieldPlainHint | undefined {
  return (
    getCheckboxWithPlainHints(field, doc) || getSelectWithPlainHints(field, doc)
  );
}

/**
 * Get stream of field plain hints
 *
 * @param field
 * @param state
 */
function getFieldPlainHint$(
  field: IField,
  state: ISPFormState,
): Observable<FieldPlainHint | undefined> {
  return state.docSubject.pipe(
    map(doc => getFieldPlainHints(field, doc)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get stream of plain hint msg for field
 *
 * @param field
 * @param state
 */
export function getFieldPlainHintMsg$(
  field: IField,
  state: ISPFormState,
): Observable<string | undefined> {
  return getFieldPlainHint$(field, state).pipe(
    switchMap(fieldPlainHints => {
      if (!fieldPlainHints) {
        return of(undefined);
      }

      return state.formService
        .formControlValueChange$(fieldPlainHints.control.$name, true)
        .pipe(
          map(
            (value: string) =>
              fieldPlainHints.plainHints[
                `hint_${fieldPlainHints.control.$name}__${value}`
              ],
          ),
        );
    }),
  );
}
